// React error boundary. Catches render-time errors so the whole app
// doesn't blank out, reports them to /api/log-error, and shows the user
// a recoverable "Something went wrong" panel.
import { Component } from 'react';
import { reportError } from '../lib/errorReporting.js';

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error?.message || 'Unknown error' };
  }

  componentDidCatch(error, info) {
    reportError({
      source: 'ErrorBoundary',
      message: error?.message,
      stack: error?.stack,
      extras: { componentStack: info?.componentStack || null },
    });
  }

  reset = () => this.setState({ hasError: false, message: null });

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-900 px-4 text-white">
        <div className="w-full max-w-md space-y-4 rounded-lg border border-red-900/50 bg-red-900/20 p-6">
          <h1 className="text-xl font-semibold text-red-200">Something went wrong</h1>
          <p className="text-sm text-red-300">
            {this.state.message}
          </p>
          <p className="text-xs text-red-200/80">
            The error has been logged. Try reloading; if it keeps happening, contact{' '}
            <a className="underline" href="mailto:hello@cognitive-compliance.co.uk">
              hello@cognitive-compliance.co.uk
            </a>.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy-900"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.reset}
              className="rounded-md border border-red-300 px-4 py-2 text-sm text-red-200"
            >
              Try to recover
            </button>
          </div>
        </div>
      </div>
    );
  }
}
